import { MedosClient } from "../client/MedosClient";
import { mapBloodGroupToApi } from "../constants";
import { WorkspaceService } from "./WorkspaceService";
import { getCurrencyByCountryCode } from "../constants";
import { generateIdempotencyKey } from "../utils/idempotencyKey";
import { resolveBrowserTimezone } from "../utils/timezone";
import { ENDPOINTS } from "../constants/endpoints";
function normalizeSlot(slot) {
    const { appointmentDate: date, fromDateTimeTs: fromTime, toDateTimeTs: toTime, } = slot;
    return {
        ...slot,
        id: slot.id,
        start: `${date}T${fromTime}:00`,
        end: `${date}T${toTime}:00`,
    };
}
function isQmsReservePayload(payload) {
    return "qmsWorkShiftId" in payload && payload.qmsWorkShiftId != null;
}
const AppointmentService = {
    async arePackagesConfigured() {
        try {
            const workspace = await WorkspaceService.fetchWorkspace();
            return workspace.arePackagesConfigured === true;
        }
        catch (error) {
            console.warn("Failed to check package configuration, defaulting to legacy endpoint:", error);
            return false;
        }
    },
    async getAddresses() {
        const res = await WorkspaceService.fetchWorkspace();
        const data = res.data ?? res;
        if (data && Array.isArray(data.addressDoctors)) {
            const addresses = data.addressDoctors
                .filter((ad) => ad.address)
                .map((ad) => {
                const addr = ad.address;
                const doctors = (ad.doctors || []).map((d) => {
                    const user = d.user;
                    const additionalDetails = user?.userAdditionalDetailResponsePayload;
                    const specializations = additionalDetails?.specializations || [];
                    const qualifications = additionalDetails?.qualifications || [];
                    return {
                        id: Number(user.id),
                        name: `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                            "Doctor",
                        email: user.email,
                        gender: user.gender,
                        countryCode: user.countryCode,
                        phoneNumber: user.phoneNumber,
                        dob: user.dob,
                        platform: user.platform,
                        isKycCompleted: user.isKycCompleted,
                        specialty: specializations.join(", ") || undefined,
                        qualifications: qualifications.join(", ") || undefined,
                        specializations,
                        consultationTypes: d.consultationTypes || [],
                    };
                });
                return {
                    id: Number(addr.id),
                    completeAddress: addr.completeAddress,
                    addressLine1: addr.addressLine1,
                    addressLine2: addr.addressLine2,
                    city: addr.city,
                    state: addr.state,
                    country: addr.country,
                    zipcode: addr.zipcode,
                    landmark: addr.landmark,
                    phoneNumber: addr.phoneNumber,
                    latitude: addr.latitude,
                    longitude: addr.longitude,
                    doctors,
                };
            });
            return {
                totalAddresses: data.totalAddresses,
                totalDoctors: data.totalDoctors,
                workspaceId: data.workspaceId,
                addresses,
                paymentPreference: data.paymentPreference || {
                    onlinePaymentEnabled: false,
                    nationalPaymentEnabled: false,
                    interNationalPaymentEnabled: false,
                },
            };
        }
        return {
            addresses: [],
            paymentPreference: {
                onlinePaymentEnabled: false,
                nationalPaymentEnabled: false,
                interNationalPaymentEnabled: false,
            },
        };
    },
    async fetchSlots(workspaceId, addressId, doctorId, appointmentDate, patientTimezone) {
        const client = await MedosClient.ensureInitialized();
        const res = await client.get(`/appointments/available-slots`, {
            params: {
                workspaceId,
                addressId,
                doctorId,
                appointmentDate,
                ...(patientTimezone ? { patientTimezone } : {}),
            },
        });
        const data = res.data ?? res;
        const responsePayload = Array.isArray(data)
            ? data
            : data?.slots && Array.isArray(data.slots)
                ? data.slots
                : [];
        if (Array.isArray(responsePayload)) {
            return responsePayload.map(normalizeSlot);
        }
        return [];
    },
    async fetchSlotsWithPacks(workspaceId, addressId, doctorId, appointmentDate, patientTimezone, durationMins, mode, followUp, patientCountryCode) {
        const client = await MedosClient.ensureInitialized();
        const res = await client.get(`/appointments/available-slots`, {
            params: {
                workspaceId,
                addressId,
                doctorId,
                appointmentDate,
                ...(durationMins ? { durationMins } : {}),
                ...(mode ? { mode } : {}),
                ...(followUp ? { followUp: true } : {}),
                ...(patientTimezone ? { patientTimezone } : {}),
                ...(patientCountryCode ? { patientCountryCode } : {}),
            },
        });
        const data = res.data ?? res;
        const slots = Array.isArray(data)
            ? data
            : data?.slots && Array.isArray(data.slots)
                ? data.slots
                : [];
        const processedSlots = slots.map(normalizeSlot);
        return {
            slots: processedSlots,
            availableDurations: Array.isArray(data?.availableDurations)
                ? data.availableDurations.filter((duration) => typeof duration === "number" && Number.isFinite(duration))
                : [],
            allSessionPackResponses: data?.allSessionPackResponses ?? [],
            plans: Array.isArray(data?.plans)
                ? data.plans.filter((plan) => typeof plan === "object" && plan !== null)
                : [],
            activeSessionPackResponses: data?.activeSessionPackResponses ?? [],
            gridDurationMins: typeof data?.gridDurationMins === "number" &&
                Number.isFinite(data.gridDurationMins)
                ? data.gridDurationMins
                : null,
            patientTimezone: data?.patientTimezone,
            patientTimezoneSource: data?.patientTimezoneSource,
        };
    },
    async fetchFollowUpEligibility(doctorId, params) {
        const client = await MedosClient.ensureInitialized();
        const res = await client.get(ENDPOINTS.appointments.followUpEligibility(doctorId), {
            params: {
                addressId: params.addressId,
                ...(params.mode ? { mode: params.mode } : {}),
                ...(params.appointmentDate
                    ? { appointmentDate: params.appointmentDate }
                    : {}),
            },
        });
        const data = res.data ?? res;
        return Array.isArray(data) ? data : [];
    },
    async fetchQmsSlots(workspaceId, addressId, doctorId, date) {
        const schedule = await this.fetchQmsSchedule(workspaceId, addressId, doctorId, date);
        return schedule.slots;
    },
    async fetchQmsSchedule(workspaceId, addressId, doctorId, date) {
        const client = await MedosClient.ensureInitialized();
        const res = await client.get(`/qms-appointments/doctor/shifts`, {
            params: {
                workspaceId,
                addressId,
                doctorId,
                date,
            },
        });
        const responseData = res.data ?? res;
        const calendar = responseData?.dates ? responseData : responseData?.data;
        const dates = calendar?.dates ?? [];
        const domesticCurrency = typeof calendar?.domesticCurrency === "string" &&
            calendar.domesticCurrency.trim()
            ? calendar.domesticCurrency.trim()
            : null;
        if (!Array.isArray(dates)) {
            return { slots: [], domesticCurrency };
        }
        const slots = dates.flatMap((d) => (d.shifts ?? [])
            .filter((shift) => shift.isEnded !== true)
            .map((shift) => ({
            ...shift,
            id: shift.qmsWorkShiftId,
            start: `${d.date}T${shift.startTime}`,
            end: `${d.date}T${shift.endTime}`,
        })));
        return { slots, domesticCurrency };
    },
    transformToUnifiedPayload(payload) {
        const bookingType = payload.bookingType || "ONE_TIME_APPOINTMENT";
        if (bookingType === "PACKAGE_PURCHASE" ||
            bookingType === "USE_ACTIVE_PACKAGE") {
            if (!payload.packageConfigId) {
                throw new Error("packageConfigId is required when bookingType is PACKAGE_PURCHASE or USE_ACTIVE_PACKAGE");
            }
            if (!payload.packageAmount && bookingType === "PACKAGE_PURCHASE") {
                throw new Error("packageAmount is required when bookingType is PACKAGE_PURCHASE");
            }
        }
        const currency = getCurrencyByCountryCode(payload.patientPayload.countryCode ?? "");
        const consultationCharge = typeof payload.consultationCharge === "string"
            ? Number.parseFloat(payload.consultationCharge) || 0
            : payload.consultationCharge || 0;
        const completeAddress = [
            payload.patientAddress.addressLine1,
            payload.patientAddress.addressLine2,
            payload.patientAddress.city,
            payload.patientAddress.state,
            payload.patientAddress.country,
            payload.patientAddress.zipcode,
        ]
            .filter(Boolean)
            .join(", ");
        const existingPatientId = Number(payload.patientPayload.id ?? payload.patientId ?? 0);
        const isExistingPatient = existingPatientId > 0;
        const unifiedPayload = {
            workspaceId: Number(payload.workspaceId || 0),
            workspaceAddressId: Number(payload.workspaceAddressId),
            doctorId: Number(payload.doctorId),
            mode: (payload.mode || "OFFLINE"),
            appointmentDate: payload.appointmentDate,
            fromDateTimeTs: payload.fromDateTimeTs,
            toDateTimeTs: payload.toDateTimeTs,
            bookingType,
            currency,
            consultationCharge,
            paymentMode: payload.paymentMode || "CASH",
            type: payload.type || "CONSULTATION",
            source: payload.source || "SDK_POWERED_WEBSITE",
            patientTimezone: payload.patientTimezone || resolveBrowserTimezone(),
            patientPayload: {
                ...(isExistingPatient ? { id: existingPatientId } : {}),
                firstName: payload.patientPayload.firstName,
                lastName: payload.patientPayload.lastName,
                email: payload.patientPayload.email || "",
                ...(payload.patientPayload.countryCode &&
                    payload.patientPayload.phoneNumber
                    ? {
                        countryCode: payload.patientPayload.countryCode,
                        phoneNumber: payload.patientPayload.phoneNumber,
                    }
                    : {}),
                dob: payload.patientPayload.dob || "",
                age: payload.patientPayload.age || 0,
                gender: (payload.patientPayload.gender || "OTHER"),
                bloodGroup: payload.patientPayload.bloodGroup
                    ? mapBloodGroupToApi(payload.patientPayload.bloodGroup)
                    : "UNKNOWN",
                patientAddress: {
                    completeAddress,
                    addressLine1: payload.patientAddress.addressLine1,
                    addressLine2: payload.patientAddress.addressLine2,
                    city: payload.patientAddress.city,
                    state: payload.patientAddress.state,
                    country: payload.patientAddress.country,
                    zipcode: payload.patientAddress.zipcode,
                    landmark: payload.patientAddress.landmark,
                    ...(payload.patientAddress.countryCode &&
                        payload.patientAddress.phoneNumber
                        ? {
                            countryCode: payload.patientAddress.countryCode,
                            phoneNumber: payload.patientAddress.phoneNumber,
                        }
                        : {}),
                },
            },
        };
        if (bookingType === "PACKAGE_PURCHASE" ||
            bookingType === "USE_ACTIVE_PACKAGE") {
            unifiedPayload.packageConfigId = payload.packageConfigId;
            unifiedPayload.packageAmount = payload.packageAmount;
        }
        if (payload.patientPackageId) {
            unifiedPayload.patientPackageId = payload.patientPackageId;
        }
        if (payload.followUp) {
            unifiedPayload.followUp = true;
        }
        const unifiedExtras = payload;
        if (unifiedExtras.currency) {
            unifiedPayload.currency = unifiedExtras.currency;
            if (unifiedExtras.paymentAmount && unifiedExtras.paymentAmount > 0) {
                unifiedPayload.paymentAmount = unifiedExtras.paymentAmount;
            }
            unifiedPayload.addressId = Number(payload.workspaceAddressId);
        }
        return unifiedPayload;
    },
    async createLegacyAppointment(payload) {
        const client = await MedosClient.ensureInitialized();
        const existingPatientId = Number(payload.patientPayload?.id ?? payload.patientId ?? 0);
        const isExistingPatient = existingPatientId > 0;
        const { id: _incomingPatientId, ...patientFields } = payload.patientPayload;
        const legacyPayload = {
            workspaceId: payload.workspaceId,
            workspaceAddressId: payload.workspaceAddressId,
            doctorId: payload.doctorId,
            mode: payload.mode || "OFFLINE",
            appointmentDate: payload.appointmentDate,
            fromDateTimeTs: payload.fromDateTimeTs,
            toDateTimeTs: payload.toDateTimeTs,
            consultationCharge: payload.consultationCharge,
            type: payload.type || "CONSULTATION",
            source: payload.source || "SDK_POWERED_WEBSITE",
            patientTimezone: payload.patientTimezone || resolveBrowserTimezone(),
            patientPayload: {
                ...(isExistingPatient ? { id: existingPatientId } : {}),
                ...patientFields,
                patientAddress: payload.patientAddress,
            },
        };
        const idempotencyKey = generateIdempotencyKey();
        if (payload.attachments && payload.attachments.length > 0) {
            const formData = new FormData();
            const payloadString = JSON.stringify(legacyPayload);
            formData.append("payload", payloadString);
            payload.attachments.forEach((file) => {
                formData.append("attachments", file);
            });
            const res = await client.post("/appointments/book-appointment", formData, {
                headers: { "Idempotency-Key": idempotencyKey },
            });
            return res.data;
        }
        else {
            const res = await client.post("/appointments/book-appointment", legacyPayload, {
                headers: {
                    "Content-Type": "application/json",
                    "Idempotency-Key": idempotencyKey,
                },
            });
            return res.data;
        }
    },
    async createAppointment(payload) {
        const packagesConfigured = await this.arePackagesConfigured();
        if (packagesConfigured) {
            return this.createUnifiedAppointment(payload);
        }
        else {
            return this.createLegacyAppointment(payload);
        }
    },
    async createUnifiedAppointment(payload) {
        const client = await MedosClient.ensureInitialized();
        const unifiedPayload = this.transformToUnifiedPayload(payload);
        const idempotencyKey = generateIdempotencyKey();
        if (payload.attachments && payload.attachments.length > 0) {
            const formData = new FormData();
            const payloadString = JSON.stringify(unifiedPayload);
            formData.append("payload", payloadString);
            payload.attachments.forEach((file) => {
                formData.append("attachments", file);
            });
            const res = await client.post("/appointments/book-appointment-unified", formData, {
                headers: { "Idempotency-Key": idempotencyKey },
            });
            return res.data;
        }
        else {
            const res = await client.post("/appointments/book-appointment-unified", unifiedPayload, {
                headers: {
                    "Content-Type": "application/json",
                    "Idempotency-Key": idempotencyKey,
                },
            });
            return res.data;
        }
    },
    async reserveAppointmentSlot(payload) {
        const client = await MedosClient.ensureInitialized();
        const unifiedPayload = this.transformToUnifiedPayload(payload);
        const res = await client.post("/appointments/reserve-slot", unifiedPayload, {
            headers: {
                "Content-Type": "application/json",
            },
        });
        return res.data;
    },
    async confirmPayment(payload) {
        const client = await MedosClient.ensureInitialized();
        const res = await client.post("/appointments/confirm-payments", payload, {
            headers: {
                "Content-Type": "application/json",
            },
        });
        return res.data;
    },
    async reserveOnlinePayment(payload, returnUrl, idempotencyKey) {
        const client = await MedosClient.ensureInitialized();
        const unifiedPayload = isQmsReservePayload(payload)
            ? payload
            : this.transformToUnifiedPayload(payload);
        const key = idempotencyKey ?? generateIdempotencyKey();
        const res = await client.post(ENDPOINTS.appointments.reserveSlot, { ...unifiedPayload, returnUrl }, {
            headers: {
                "Content-Type": "application/json",
                "Idempotency-Key": key,
            },
        });
        return res.data;
    },
    async confirmOnlinePayment(payload) {
        const client = await MedosClient.ensureInitialized();
        const res = await client.post(ENDPOINTS.appointments.confirmPayment, payload, { headers: { "Content-Type": "application/json" } });
        return res.data;
    },
};
export { AppointmentService };
